import React, { useMemo, useRef, useState, useEffect } from 'react';

/**
 * Lightweight autocomplete text input.
 * - options: array of { value: string, hint?: string }  (hint = subtitle, e.g. phone)
 * - value: controlled string
 * - onChange: (newValue) => void
 * - testIdPrefix: data-testid prefix
 * - maxSuggestions: limit
 */
const AutocompleteInput = ({
  value,
  onChange,
  options = [],
  placeholder = '',
  type = 'text',
  required = false,
  testIdPrefix,
  maxSuggestions = 8,
  inputClassName,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Dedup + sort options
  const uniqueOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const opt of options) {
      const v = (opt?.value || '').trim();
      if (!v) continue;
      const key = v.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ value: v, hint: opt.hint || '' });
    }
    return out;
  }, [options]);

  const suggestions = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    const filtered = q
      ? uniqueOptions.filter(
          (o) => o.value.toLowerCase().includes(q) || (o.hint || '').toLowerCase().includes(q)
        )
      : uniqueOptions;
    return filtered.slice(0, maxSuggestions);
  }, [uniqueOptions, value, maxSuggestions]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type={type}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        className={inputClassName}
        data-testid={`${testIdPrefix}-input`}
      />
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-30 mt-1 w-full bg-white rounded-lg shadow-lg overflow-hidden"
          style={{ border: '1px solid #E5E0D8', maxHeight: 220, overflowY: 'auto' }}
          data-testid={`${testIdPrefix}-suggestions`}
        >
          {suggestions.map((opt) => (
            <li
              key={opt.value}
              onMouseDown={(e) => { e.preventDefault(); onChange(opt.value); setOpen(false); }}
              className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm flex justify-between items-center"
              data-testid={`${testIdPrefix}-suggestion-${opt.value.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <span style={{ color: '#2D3A2A' }}>{opt.value}</span>
              {opt.hint && <span className="text-xs" style={{ color: '#6B7280' }}>{opt.hint}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AutocompleteInput;
